// [S-091] Tenants — platform_admin provisioning + browse.
//
// Lists every provisioned tenant and exposes the Provision dialog.
// Tenant rows link to /admin/tenants/:id/users for the user-management
// view. Wireframe MISSING — list table follows the existing
// .tbl--audit pattern; the dialog follows the re-attestation Schedule
// dialog's structure (label + input + footer buttons).
//
// Role gate: <RequireRole minRole="platform_admin"> wraps the route in
// App.tsx. TenantService.listTenants ALSO gates server-side — defense in
// depth, per the architecture rules.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Dialog,
  LoadingState,
  PageHead,
  Pagination,
  Select,
  usePagination,
} from "../components";
import { getApiErrorMessage } from "../data/api";
import {
  useProvisionTenant,
  useTenantsList,
  type ProvisionTenantInput,
  type TenantRow,
} from "../data/tenantsQuery";
import { useIdentityLabels } from "../data/identitiesQuery";

const DEPLOYMENT_MODES: ReadonlyArray<{
  value: ProvisionTenantInput["deploymentMode"];
  label: string;
}> = [
  { value: "saas", label: "SaaS" },
  { value: "hybrid", label: "Hybrid" },
  { value: "on_prem", label: "On-prem" },
];

const ACCESS_TIERS: ReadonlyArray<{
  value: ProvisionTenantInput["accessTier"];
  label: string;
}> = [
  { value: "basic", label: "Basic" },
  { value: "professional", label: "Professional" },
  { value: "enterprise", label: "Enterprise" },
];

export function Tenants(): JSX.Element {
  const { data, isLoading, isError, refetch, isFetching } = useTenantsList();
  const [provisionOpen, setProvisionOpen] = useState(false);
  // Called unconditionally (before the loading/error early returns) per the
  // rules of hooks — degrades to an empty uid list pre-load.
  const provisionerLabels = useIdentityLabels(
    data?.tenants.map((t) => t.provisionedBy) ?? []
  ).data;
  const pager = usePagination(data?.tenants ?? [], 25);
  const showLoading = isLoading || data === undefined;

  return (
    <>
      <PageHead
        eyebrow="Platform · organizations"
        title={
          <>
            Every <em>studio</em>, one console.
          </>
        }
        sub={
          showLoading
            ? "Fetching provisioned tenants."
            : isError
              ? "The admin API responded with an error. Try again in a moment."
              : "Provision a new organization, inspect existing organizations, and jump to an organization's user-management surface."
        }
        cta={
          !showLoading && !isError ? (
            <>
              <span className="reatt__head__refresh">
                {data.tenants.length}{" "}
                {data.tenants.length === 1 ? "tenant" : "tenants"}
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
                data-testid="tnt-refresh"
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                variant="primary"
                className="btn--s"
                onClick={() => setProvisionOpen(true)}
                data-testid="tnt-provision-open"
              >
                Provision tenant
              </Button>
            </>
          ) : undefined
        }
      />

      {showLoading && <LoadingState label="Loading organizations…" />}

      {!showLoading && isError && (
        <Alert label="Organizations unavailable" tone="error">
          The admin API responded with an error. Try again in a moment.
        </Alert>
      )}

      {!showLoading && !isError && (
        <>
          {data.tenants.length === 0 ? (
            <EmptyState onProvision={() => setProvisionOpen(true)} />
          ) : (
            <div className="tbl tbl--audit tbl--tenants" data-testid="tnt-table">
              <div className="tbl__row tbl__row--head">
                <span>Organization</span>
                <span>Mode</span>
                <span>Tier</span>
                <span>Provisioned by</span>
                <span>Created</span>
                <span>Users</span>
              </div>
              {pager.pageItems.map((row) => (
                <TenantRowView
                  key={row.id}
                  row={row}
                  provisionerLabel={provisionerLabels?.get(row.provisionedBy)}
                />
              ))}
            </div>
          )}

          {data.tenants.length > 0 && (
            <Pagination
              page={pager.page}
              pageCount={pager.pageCount}
              onPageChange={pager.setPage}
              total={pager.total}
              rangeStart={pager.rangeStart}
              rangeEnd={pager.rangeEnd}
              itemLabel="tenant"
            />
          )}
        </>
      )}

      <ProvisionDialog
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
      />
    </>
  );
}

function TenantRowView({
  row,
  provisionerLabel,
}: {
  row: TenantRow;
  provisionerLabel: string | undefined;
}): JSX.Element {
  return (
    <div className="tbl__row" data-testid={`tnt-row-${row.id}`}>
      <div className="tnt__id">
        <span className="src-edge src-edge--stud" aria-hidden="true" />
        <span>{row.studioId}</span>
      </div>
      <span className="tnt__mode">{prettyMode(row.deploymentMode)}</span>
      <span>
        <span className={`tnt__tier tnt__tier--${row.accessTier}`}>
          {row.accessTier}
        </span>
      </span>
      <span className="tnt__who">{provisionerLabel ?? "—"}</span>
      <span className="tnt__when">{formatShortDate(row.createdAt)}</span>
      <span className="tnt__actions">
        <Link
          to={`/admin/tenants/${encodeURIComponent(row.id)}/users`}
          className="btn btn--s"
          data-testid={`tnt-users-link-${row.id}`}
        >
          Users
        </Link>
      </span>
    </div>
  );
}

function EmptyState({
  onProvision,
}: {
  onProvision: () => void;
}): JSX.Element {
  return (
    <div className="tnt__empty" data-testid="tnt-empty">
      <b>No tenants provisioned yet</b>
      Provision the first studio to start onboarding their team.
      <div style={{ marginTop: 12 }}>
        <Button
          variant="primary"
          className="btn--s"
          onClick={onProvision}
          data-testid="tnt-empty-provision"
        >
          Provision tenant
        </Button>
      </div>
    </div>
  );
}

function ProvisionDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const mutation = useProvisionTenant();
  const [studioName, setStudioName] = useState("");
  const [deploymentMode, setDeploymentMode] =
    useState<ProvisionTenantInput["deploymentMode"]>("saas");
  const [accessTier, setAccessTier] =
    useState<ProvisionTenantInput["accessTier"]>("professional");
  const [email, setEmail] = useState("");

  const reset = (): void => {
    setStudioName("");
    setDeploymentMode("saas");
    setAccessTier("professional");
    setEmail("");
    mutation.reset();
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const submitDisabled =
    mutation.isPending ||
    studioName.trim().length === 0 ||
    email.trim().length === 0;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      title={<span>Provision a new organization</span>}
      description={
        <span>
          Creates the organization document and a tenant_admin Identity Platform
          user. The admin gets an invite email at the address below.
        </span>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(
            {
              studioName: studioName.trim(),
              deploymentMode,
              accessTier,
              initialTenantAdminEmail: email.trim(),
            },
            {
              onSuccess: handleClose,
            }
          );
        }}
      >
        <div className="tnt__form__row">
          <label htmlFor="tnt-studio-name">Studio name</label>
          <input
            id="tnt-studio-name"
            type="text"
            value={studioName}
            onChange={(e) => setStudioName(e.target.value)}
            placeholder="e.g. Meridian Pictures"
            required
            data-testid="tnt-form-studio-name"
          />
        </div>
        <div className="tnt__form__row">
          <label>Deployment mode</label>
          <Select
            label="Deployment mode"
            value={deploymentMode}
            options={DEPLOYMENT_MODES}
            onValueChange={(v) => {
              if (v !== undefined) setDeploymentMode(v);
            }}
            clearable={false}
          />
        </div>
        <div className="tnt__form__row">
          <label>Access tier</label>
          <Select
            label="Access tier"
            value={accessTier}
            options={ACCESS_TIERS}
            onValueChange={(v) => {
              if (v !== undefined) setAccessTier(v);
            }}
            clearable={false}
          />
        </div>
        <div className="tnt__form__row">
          <label htmlFor="tnt-admin-email">
            Initial tenant_admin email
          </label>
          <input
            id="tnt-admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@studio.example"
            required
            data-testid="tnt-form-admin-email"
          />
        </div>

        {mutation.isError && (
          <div className="tnt__form__error" role="alert" data-testid="tnt-form-error">
            {getApiErrorMessage(mutation.error) ??
              "Could not provision the organization. Check the inputs and try again."}
          </div>
        )}

        <div className="tnt__form__ft">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={mutation.isPending}
            type="button"
            data-testid="tnt-form-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitDisabled}
            data-testid="tnt-form-submit"
          >
            {mutation.isPending ? "Provisioning…" : "Provision"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function prettyMode(m: TenantRow["deploymentMode"]): string {
  return m === "on_prem" ? "On-prem" : m === "saas" ? "SaaS" : "Hybrid";
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
